import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";
import axios from "axios";

export default function Subcategory() {
  const navigate = useNavigate();
  const location = useLocation();
  const controls = useAnimation();
  const queryParams = new URLSearchParams(location.search);
  const selectedCategoryId = queryParams.get("id");
  const [subcategory, setSubcategory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }, []);

  // Memoized filtered categories to prevent unnecessary re-renders
  const filteredSubCategories = useMemo(() => {
    return subcategory.filter(cat => cat.category?._id === selectedCategoryId);
  }, [selectedCategoryId, subcategory]);

  // Optimized background elements with reduced count
  const backgroundElements = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => ({
      id: i,
      size: 100 + i * 20,
      top: 10 + i * 15,
      left: 10 + (i * 15) % 70,
      duration: 25 + i * 5
    }));
  }, []);

  const getSubcategory = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        "https://wavetechservices.in/api/user/subcategories"
      );
      setSubcategory(response.data.data);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSubcategory();
  }, []);

  useEffect(() => {
    if (filteredSubCategories.length > 0) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { staggerChildren: 0.05, ease: "easeOut" }
      });
    }
  }, [filteredSubCategories, controls]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center p-8">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800">Loading Subcategories...</h2>
        </div>
      </div>
    );
  }

  if (filteredSubCategories.length === 0 && !isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", damping: 10 }}
          className="text-center p-8 bg-white rounded-2xl shadow-xl border border-gray-100"
        >
          <h2 className="text-3xl font-bold text-gray-800 mb-4">No Subcategories Found</h2>
          <p className="text-gray-600 mb-6">We couldn't find any subcategories for this selection.</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg"
          >
            Go Back
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen mt-10 bg-gradient-to-br from-gray-50 to-gray-100 py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Optimized Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {backgroundElements.map((el) => (
          <motion.div
            key={el.id}
            initial={{ opacity: 0.1 }}
            animate={{
              x: [0, 50 * (el.id % 2 === 0 ? 1 : -1)],
              y: [0, 30 * (el.id % 3 === 0 ? 1 : -1)],
              rotate: [0, 180],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{
              duration: el.duration,
              repeat: Infinity,
              ease: "linear",
              repeatType: "reverse"
            }}
            className="absolute rounded-full bg-gradient-to-r from-blue-100 to-purple-100"
            style={{
              width: `${el.size}px`,
              height: `${el.size}px`,
              top: `${el.top}%`,
              left: `${el.left}%`,
              filter: "blur(30px)"
            }}
          />
        ))}
      </div>

      {/* Header Section */}
{/* Header Section */}
<motion.div
  initial={{ opacity: 0, y: -20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.5 }}
  className="text-center relative z-10"
>
  <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
    Explore <span className="text-transparent bg-clip-text text-gradient from-blue-500 to-purple-600">Subcategories</span>
  </h1>
  <p className="text-lg text-gray-600 max-w-3xl mx-auto">
    Discover premium services tailored to your needs
  </p>

  {/* Crazy Animated Category Title */}
  <motion.div
    initial={{ scale: 0.5, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ 
      delay: 0.3,
      type: "spring",
      stiffness: 200,
      damping: 10
    }}
    className="mt-10 mb-8 relative inline-block"
  >
    <motion.div
      animate={{
        rotate: [0, 5, -5, 5, 0],
        scale: [1, 1.05, 1, 1.03, 1]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 blur-xl rounded-full"
    />
    
    <motion.h1
      whileHover={{
        scale: 1.05,
        textShadow: "0 0 10px rgba(124, 58, 237, 0.5)"
      }}
      className="relative text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-full border-2 border-purple-200/50 inline-block"
    >
      {filteredSubCategories[0]?.category?.name || "Premium"} Category
       
    </motion.h1>
     
  </motion.div>
</motion.div>

      {/* Categories Grid - Using will-change for performance */}
      <motion.div
        initial="hidden"
        animate={controls}
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 relative z-10 max-w-7xl mx-auto"
        style={{ willChange: "transform" }}
      >
        {filteredSubCategories.map((category, index) => (
          <motion.div
            key={category._id}
            custom={index}
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={controls}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 15,
              mass: 0.5
            }}
            onClick={() => navigate(`/service?id=${encodeURIComponent(category._id)}`)}
            className="group cursor-pointer will-change-transform"
          >
            <div className="relative overflow-hidden rounded-xl bg-white shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-100 group-hover:border-blue-100">
              {/* Optimized Image Container with lazy loading */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={category.image || "https://via.placeholder.com/300"}
                  alt={category.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    e.target.src = "https://via.placeholder.com/300";
                  }}
                />
                {/* Simplified Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>

              {/* Category Name */}
              <div className="p-4 relative">
                <h3 className="font-semibold text-lg text-gray-800 text-center">
                  {category.name}
                </h3>
                <motion.div
                  initial={{ width: 0 }}
                  whileHover={{ width: "100%" }}
                  className="h-0.5 mx-auto bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-300 mt-2"
                />
                 
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Optimized Floating Action Button */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-20"
      >
        <button
          onClick={() => navigate(-1)}
          className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg focus:outline-none"
          aria-label="Go back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
      </motion.div>
    </div>
  );
}